import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/styles';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import queryString from 'query-string';
import { useLazyQuery } from '@apollo/client';
import { Route, Redirect, Switch, useHistory, matchPath, useLocation } from 'react-router-dom';
import Auth from '~pages/auth';
import AgentWrapper from '~pages/agent-login/agent-wrapper';
import Onboarding from '~pages/onboarding';
import { QUERY_LANDLORD_INFO } from './queries';
import Comments from '~pages/pms-dashboard/comments';
import HelpCenter from '~pages/pms-dashboard/help-center';
import NewHelpCenter from '~pages/pms-dashboard/new-help-center';
import RouterComponents from './route-components';
import { landlordStatus } from 'common/constants/landlord';
import SpinLoading from 'common/components/spin-loading';
import getClientInfo from '~helpers/getClientInfo';
import generatePath from '~settings/routing';
import { setLandlordProperties, setLandlordName } from '~actions/auth';

const uesStyles = makeStyles(
  () => ({
    loading: {
      height: '100vh',
    },
  }),
  { name: 'Router' },
);

const HomePageRouters = ({
  landlordId,
  hasKeyProperty,
  isOnboardingStatus,
  pathname,
  refetchQueryLandlordCompanyInfo,
}) => {
  const history = useHistory();
  const { t } = useTranslation();

  const atOnboardingPage = useMemo(() => !!matchPath(pathname, '/onboarding'), [pathname]);

  useEffect(() => {
    if (pathname === '/' || (atOnboardingPage && !isOnboardingStatus)) {
      history.replace('/board');
    }
  }, [atOnboardingPage, history, isOnboardingStatus, pathname]);

  return (
    <Switch>
      <Route path="/onboarding">
        <Onboarding
          landlordId={landlordId}
          refetchQueryLandlordCompanyInfo={refetchQueryLandlordCompanyInfo}
        />
      </Route>
      <Route path="/comments/:propertySlug" component={Comments} />
      <Route path="/help-center" component={HelpCenter} />
      <Route path="/new-help-center" component={NewHelpCenter} />
      <RouterComponents t={t} hasKeyProperty={hasKeyProperty} />
    </Switch>
  );
};

HomePageRouters.propTypes = {
  landlordId: PropTypes.string.isRequired,
  isOnboardingStatus: PropTypes.bool.isRequired,
  pathname: PropTypes.string.isRequired,
  refetchQueryLandlordCompanyInfo: PropTypes.func.isRequired,
};

const Router = () => {
  const classes = uesStyles();
  const history = useHistory();
  const Location = useLocation();
  const dispatch = useDispatch();

  const { pathname } = Location;
  const { token } = useSelector(state => ({
    token:
      matchPath(pathname, '/agent-login') ||
      matchPath(pathname, '/reset-agent-password') ||
      matchPath(pathname, '/set-agent-password')
        ? ''
        : state.auth.getIn(['base', 'token']),
  }));

  const [queryLandlordCompanyInfo, { data, refetch: refetchQueryLandlordCompanyInfo }] =
    useLazyQuery(QUERY_LANDLORD_INFO, {
      fetchPolicy: 'no-cache',
    });

  const queryInfo = queryString.parse(history?.location?.search);

  const isInVerifyEmailPage = !!queryInfo['verify-email-token'];

  // refetch will not call onComplete again, so use effect to instead
  useEffect(() => {
    if (data) {
      dispatch(setLandlordProperties(data.viewer));

      if (data?.viewer && (data?.viewer?.firstName || data?.viewer?.lastName)) {
        dispatch(setLandlordName(data.viewer));
      }
      if (data?.viewer?.company?.status === landlordStatus.ONBOARDING) {
        if (!isInVerifyEmailPage) {
          history.replace('/onboarding');
        }
      }
    }
  }, [data, dispatch, history, isInVerifyEmailPage]);

  let landlordInfo = data?.viewer;

  const isOnboardingStatus = landlordInfo?.company?.status === landlordStatus.ONBOARDING;

  const atAuthPage = useMemo(
    () =>
      !!matchPath(pathname, '/login') ||
      !!matchPath(pathname, '/agent-login') ||
      !!matchPath(pathname, '/reset-agent-password') ||
      !!matchPath(pathname, '/reset-password') ||
      !!matchPath(pathname, '/set-password') ||
      !!matchPath(pathname, '/set-agent-password'),
    [pathname],
  );

  useEffect(() => {
    if (!atAuthPage && !token) {
      const url = generatePath('login', {});
      window.open(url, '_self');
    }

    if (atAuthPage && token) {
      history.replace('/');
    }
  }, [atAuthPage, history, token]);

  useEffect(() => {
    if (token) {
      queryLandlordCompanyInfo();
    }
  }, [queryLandlordCompanyInfo, token]);

  useEffect(() => {
    getClientInfo();
    // Force use trak and only render when first refresh
    if (window.location.search) {
      const query = queryString.parse(window.location.search);
      if (query?.use_trak === 'yes') {
        window.hurricane = {
          useTrak: true,
          ...window.hurricane,
        };
      }
    }
  }, []);

  return (
    <>
      <Switch>
        <Route path="/login" component={Auth} />
        <Route exact path="/agent-login" component={AgentWrapper} />
        <Route path="/reset-agent-password/:email?" component={AgentWrapper} />
        <Route path="/set-agent-password" component={AgentWrapper} />
        <Route path="/reset-password/:email?" component={Auth} />
        <Route path="/set-password" component={Auth} />
        <Route path="/">
          <Choose>
            <When condition={!!landlordInfo}>
              <If condition={isInVerifyEmailPage && isOnboardingStatus}>
                <Onboarding
                  landlordId={landlordInfo?.company?.id}
                  refetchQueryLandlordCompanyInfo={refetchQueryLandlordCompanyInfo}
                />
              </If>
              <If condition={!(isInVerifyEmailPage && isOnboardingStatus)}>
                <HomePageRouters
                  landlordId={landlordInfo?.company?.id}
                  hasKeyProperty={landlordInfo?.company?.hasPropertyForKey}
                  isOnboardingStatus={isOnboardingStatus}
                  pathname={pathname}
                  refetchQueryLandlordCompanyInfo={refetchQueryLandlordCompanyInfo}
                />
              </If>
            </When>
            <Otherwise>
              <SpinLoading className={classes.loading} />
            </Otherwise>
          </Choose>
        </Route>
        <Redirect to="/" />
      </Switch>
    </>
  );
};

export default Router;
